/**
 * EnvironmentsSection - the persistent workspaces of a project, in two halves.
 *
 * **Personales** is the older one: the long-lived workspace of a `(user,
 * project)` pair, handed out by an administrator. It is a table, because the
 * question it answers is "who has one here, and may I take it away?" — an
 * account-shaped question, gated on `u.view`/`u.edit`.
 *
 * **Del proyecto** is the shared one: `qa`, `staging`, the places a team keeps
 * test data in. It is governed by `p.env.use` — the same grant that lets a
 * thread run inside one — because a PM who is not an administrator has to be
 * able to see the `qa` card and open a thread in it. Its data comes from
 * `/api/environments`, not from the admin route, for exactly that reason.
 *
 * The rule this screen exists to respect: a control placed inside a container
 * governed by one permission inherits that permission by accident. So the tab
 * is `p.env.use`, creating and destroying are `p.env.manage`, listing snapshots
 * is `u.view`, and each button asks for its own.
 */
import type {
  EnvironmentId,
  ProjectId,
  QaOperationKind,
  ThreadId,
  UserId,
} from "@taskin-code/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useAuthStore, useHasProjectPermission } from "../../auth/authStore";
import {
  type AdminEnvironmentRow,
  type AdminEnvironmentSnapshots,
  EnvironmentsClient,
} from "../../auth/EnvironmentsClient";
import { adminFetch } from "../../lib/adminFetch";
import { environmentQueryKeys } from "../../lib/environmentQueries";
import { openNewThreadDialog } from "../../newThreadDialogStore";
import { useStore } from "../../store";
import { useTerminalStateStore } from "../../terminalStateStore";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { UserAvatar } from "../UserAvatar";
import {
  eligibleOwners,
  environmentStatusLabel,
  ownerCanUseEnvironment,
  previewUrl,
  sortEnvironments,
} from "./EnvironmentsSection.logic";
import { NewProjectEnvironmentDialog } from "./NewProjectEnvironmentDialog";
import { ProjectEnvironmentCard } from "./ProjectEnvironmentCard";
import { personalEnvironmentsOf, projectEnvironmentsOf } from "./projectEnvironments.logic";
import { Field, ModalShell } from "./settingsModal";
import { SnapshotList } from "./SnapshotList";

interface AdminUserRow {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly avatarFileName: string | null;
  readonly projectAssignments?: ReadonlyArray<{
    readonly projectId: string;
    readonly permissions: ReadonlyArray<string>;
  }>;
}

type TabId = "personal" | "project";

const STATUS_CLASS: Record<AdminEnvironmentRow["status"], string> = {
  ready: "text-emerald-400",
  provisioning: "text-amber-400",
  failed: "text-red-400",
};

/**
 * @param projectId  When given, the panel loses its own picker and its heading:
 *   Settings → Proyectos already fixes which project this is about.
 */
export function EnvironmentsSection(props: { readonly projectId?: ProjectId } = {}) {
  const isAdmin = useAuthStore((state) => state.globalPermissions.has("u.view"));
  const canEdit = useAuthStore((state) => state.globalPermissions.has("u.edit"));
  const projects = useStore((state) => state.projects);
  const navigate = useNavigate();
  const openChatThreadPage = useTerminalStateStore((state) => state.openChatThreadPage);

  const fixedProjectId = props.projectId;
  const [selectedProjectId, setSelectedProjectId] = useState<ProjectId | null>(null);
  const projectId = fixedProjectId ?? selectedProjectId;
  const canUseEnvironments = useHasProjectPermission(projectId, "p.env.use");
  const canManageEnvironments = useHasProjectPermission(projectId, "p.env.manage");
  // Changing what is inside an environment — its code or its data. Separate
  // from `p.env.use`, which is only "may I work in it".
  const canSyncEnvironments = useHasProjectPermission(projectId, "p.env.sync");
  // Its own grant, not a corner of `p.env.sync`: putting somebody's unreviewed
  // pull request on the environment everybody shares is a different decision
  // from bringing in the branch the project already agreed on.
  const canPreviewPullRequests = useHasProjectPermission(projectId, "p.pr.preview");
  const [error, setError] = useState<string | null>(null);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [projectProvisionOpen, setProjectProvisionOpen] = useState(false);
  const [busyId, setBusyId] = useState<EnvironmentId | null>(null);
  const [tab, setTab] = useState<TabId>(isAdmin ? "personal" : "project");

  // The first project is a better default than an empty screen, and the picker
  // is right there for the other case.
  useEffect(() => {
    if (fixedProjectId !== undefined || projectId !== null) return;
    const first = projects[0];
    if (first !== undefined) setSelectedProjectId(first.id as ProjectId);
  }, [projects, projectId, fixedProjectId]);

  // Through React Query rather than a manual `refresh()`, so the section is on
  // the same rail as the rest of the app: `__root` invalidates
  // `environmentQueryKeys.all` when an `environment.*` domain event arrives, and
  // the 10-minute sweep's `environment.sync-state-changed` lands on the screen
  // without anybody pressing anything.
  //
  // An administrator sees every environment of the project; anybody else sees
  // the ones they may work in, which is what the shared tab is about.
  const queryClient = useQueryClient();
  const environmentsQuery = useQuery({
    queryKey: environmentQueryKeys.list(projectId, isAdmin ? "admin" : "usable"),
    queryFn: async (): Promise<ReadonlyArray<AdminEnvironmentRow>> => {
      if (projectId === null) return [];
      return isAdmin
        ? EnvironmentsClient.listByProject(projectId)
        : EnvironmentsClient.listUsable(projectId);
    },
    enabled: projectId !== null && (isAdmin || canUseEnvironments),
    // Taking and releasing an environment's lock is deliberately NOT a domain
    // event — it is in-process state that a crash must not outlive — so nothing
    // pushes «Actualizando… por X» onto the card. Ten seconds while this tab is
    // mounted is what keeps it honest; the endpoint reads stored rows and runs
    // no `git fetch`, so it is a cheap question to ask.
    refetchInterval: 10_000,
    retry: false,
  });
  // The roster only decorates the Personales table, and only an administrator
  // can read it at all.
  const usersQuery = useQuery({
    queryKey: environmentQueryKeys.roster(),
    queryFn: async (): Promise<ReadonlyArray<AdminUserRow>> =>
      (await adminFetch<{ users: ReadonlyArray<AdminUserRow> }>("/api/admin/users")).users,
    enabled: isAdmin,
    retry: false,
  });

  const environments = environmentsQuery.data ?? [];
  const users = usersQuery.data ?? [];
  const loading = environmentsQuery.isPending;
  const loadError =
    environmentsQuery.error instanceof Error
      ? environmentsQuery.error.message
      : environmentsQuery.error !== null
        ? "No se pudieron cargar los entornos"
        : null;
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: environmentQueryKeys.all });
  };

  // A tab nobody may open must not be the one showing.
  useEffect(() => {
    if (tab === "personal" && !isAdmin) setTab("project");
    if (tab === "project" && !canUseEnvironments) setTab(isAdmin ? "personal" : "project");
  }, [tab, isAdmin, canUseEnvironments]);

  if (!isAdmin && !canUseEnvironments) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        Necesitas <code>u.view</code> para administrar entornos, o <code>p.env.use</code> en este
        proyecto para ver los que puedes usar.
      </div>
    );
  }

  const nameOf = (userId: string) =>
    users.find((user) => user.userId === userId)?.name ?? "Usuario desconocido";
  const personal = personalEnvironmentsOf(environments);
  const sorted = sortEnvironments(personal, (row) => nameOf(row.userId ?? ""));
  const projectEnvironments = projectEnvironmentsOf(environments);
  const selectedProject = projects.find((project) => project.id === projectId) ?? null;
  const baseBranch = selectedProject?.baseBranch ?? null;

  const destroy = async (row: AdminEnvironmentRow) => {
    const what =
      row.kind === "project"
        ? `el entorno ${row.name ?? row.branch} de este proyecto`
        : `el entorno de ${nameOf((row.userId ?? "") as string)}`;
    if (
      !window.confirm(
        `¿Destruir ${what}? Se borran su worktree, sus bases de datos sandbox y todas sus copias. Los hilos que lo usaban se quedan sin espacio de trabajo.`,
      )
    ) {
      return;
    }
    setError(null);
    setBusyId(row.id);
    try {
      await EnvironmentsClient.destroy(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo destruir el entorno");
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Go to an existing thread — the one holding the environment, or the one it
   * was last changed from.
   *
   * Client navigation, never an `<a href>`: this screen is a route inside the
   * app, and a full page load would drop the WebSocket session and every draft
   * in flight to arrive somewhere the router can go directly. The two steps are
   * the sidebar's — register the page, then navigate.
   */
  const openThreadPage = (threadId: ThreadId) => {
    openChatThreadPage(threadId);
    void navigate({ to: "/$threadId", params: { threadId } });
  };

  const openThreadIn = (environmentId: EnvironmentId) => {
    if (projectId === null) return;
    openNewThreadDialog({ preferredProjectId: projectId, environmentId });
  };

  /**
   * The same dialog, opened in its «operación de entorno» mode: the project and
   * the environment stop being choices, the message is already written, and the
   * agent list narrows to the agents that can actually run the operation.
   */
  const openOperationThread = (input: {
    readonly environmentId: EnvironmentId;
    readonly operation: QaOperationKind;
    readonly prompt: string;
  }) => {
    if (projectId === null) return;
    openNewThreadDialog({
      preferredProjectId: projectId,
      environmentId: input.environmentId,
      qaOperation: input.operation,
      initialPrompt: input.prompt,
    });
  };

  const tabs: ReadonlyArray<{ readonly id: TabId; readonly label: string }> = [
    ...(isAdmin ? [{ id: "personal" as const, label: "Personales" }] : []),
    ...(canUseEnvironments ? [{ id: "project" as const, label: "Del proyecto" }] : []),
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="min-w-0">
        {fixedProjectId === undefined && (
          <h3 className="text-base font-semibold">Entornos persistentes</h3>
        )}
        <p className="mt-0.5 text-xs text-muted-foreground">
          Espacios de trabajo permanentes: su rama, su preview y sus bases de datos sandbox, que
          sobreviven al archivado de los hilos. Los personales son de una persona; los del proyecto
          los comparte todo el equipo.
        </p>
      </div>

      {fixedProjectId === undefined && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Proyecto</span>
          <Select
            value={projectId ?? ""}
            onValueChange={(value) => {
              if (!value) return;
              setSelectedProjectId(value as ProjectId);
            }}
          >
            <SelectTrigger className="w-full sm:w-96" aria-label="Proyecto">
              <SelectValue>{selectedProject?.name ?? "Elige un proyecto"}</SelectValue>
            </SelectTrigger>
            <SelectPopup align="start" alignItemWithTrigger={false}>
              {projects.map((project) => (
                <SelectItem hideIndicator key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
        </div>
      )}

      {tabs.length > 1 ? (
        <div
          role="tablist"
          aria-label="Tipo de entorno"
          className="flex flex-wrap gap-1 border-b border-border/60"
        >
          {tabs.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              onClick={() => setTab(entry.id)}
              className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
                tab === entry.id
                  ? "border-brand text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {entry.label}
            </button>
          ))}
        </div>
      ) : null}

      {(error ?? loadError) ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {error ?? loadError}
        </div>
      ) : null}

      {tab === "personal" && isAdmin ? (
        <div className="flex flex-col gap-4">
          <div className="flex justify-end">
            <Button
              size="sm"
              className="shrink-0"
              disabled={!canEdit || projectId === null}
              onClick={() => setProvisionOpen(true)}
            >
              + Nuevo entorno
            </Button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border/60 bg-card/40">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-b border-border/40 bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-[24%] px-4 py-2 font-medium">Dueño</th>
                  <th className="w-[12%] px-4 py-2 font-medium">Estado</th>
                  <th className="px-4 py-2 font-medium">Rama</th>
                  <th className="w-[16%] px-4 py-2 font-medium">Preview</th>
                  <th className="w-[1%] px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {projectId === null ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-xs text-muted-foreground">
                      No hay proyectos todavía.
                    </td>
                  </tr>
                ) : loading && sorted.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-xs text-muted-foreground">
                      Cargando entornos…
                    </td>
                  </tr>
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-xs text-muted-foreground">
                      Nadie tiene entorno en este proyecto.
                    </td>
                  </tr>
                ) : (
                  sorted.map((row) => (
                    <EnvironmentRow
                      key={row.id}
                      row={row}
                      owner={users.find((user) => user.userId === (row.userId as string)) ?? null}
                      ownerMayUse={ownerCanUseEnvironment(
                        users.find((user) => user.userId === (row.userId as string))
                          ?.projectAssignments,
                        row.projectId as string,
                      )}
                      canEdit={canEdit}
                      busy={busyId === row.id}
                      onOpenThread={openThreadIn}
                      onDestroy={() => void destroy(row)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === "project" && canUseEnvironments ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="min-w-0 text-xs text-muted-foreground">
              Nacen de la rama base del proyecto y la siguen. Actualizarlos con esa rama es trabajo
              de un agente, para que quede el hilo como registro.
            </p>
            {canManageEnvironments ? (
              <Button
                size="sm"
                className="shrink-0"
                disabled={projectId === null || baseBranch === null}
                title={
                  baseBranch === null
                    ? "Este proyecto no tiene rama base: defínela en la pestaña Repositorio."
                    : undefined
                }
                onClick={() => setProjectProvisionOpen(true)}
              >
                + Nuevo entorno del proyecto
              </Button>
            ) : null}
          </div>

          {canManageEnvironments && baseBranch === null ? (
            <p className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
              Este proyecto todavía no tiene rama base. Defínela en Repositorio y desde ahí nacerán
              los entornos del proyecto y las previews de pull request.
            </p>
          ) : null}

          {loading && projectEnvironments.length === 0 ? (
            <p className="text-xs text-muted-foreground">Cargando entornos…</p>
          ) : projectEnvironments.length === 0 ? (
            <div className="rounded-lg border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
              Este proyecto no tiene entornos compartidos.
              {canManageEnvironments
                ? " Crea uno para que el equipo tenga un sitio estable donde probar."
                : " Pídele a quien lo administre que cree uno."}
            </div>
          ) : (
            projectEnvironments.map((row) => (
              <ProjectEnvironmentCard
                key={row.id}
                row={row}
                projectId={row.projectId}
                known={environments}
                canManage={canManageEnvironments}
                canSync={canSyncEnvironments}
                canPreview={canPreviewPullRequests}
                busy={busyId === row.id}
                onOpenThread={openThreadIn}
                onOpenOperation={openOperationThread}
                onOpenThreadPage={openThreadPage}
                onDestroy={() => void destroy(row)}
              />
            ))
          )}
        </div>
      ) : null}

      {provisionOpen && projectId !== null ? (
        <ProvisionDialog
          projectId={projectId}
          projectName={selectedProject?.name ?? "este proyecto"}
          candidates={eligibleOwners(users, environments)}
          onClose={() => setProvisionOpen(false)}
          onDone={() => {
            setProvisionOpen(false);
            void refresh();
          }}
        />
      ) : null}

      {projectProvisionOpen && projectId !== null ? (
        <NewProjectEnvironmentDialog
          projectId={projectId}
          projectName={selectedProject?.name ?? "este proyecto"}
          baseBranch={baseBranch}
          existing={projectEnvironments}
          sources={environments.filter((row) => row.status === "ready")}
          onClose={() => setProjectProvisionOpen(false)}
          onDone={() => {
            setProjectProvisionOpen(false);
            void refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function EnvironmentRow(props: {
  row: AdminEnvironmentRow;
  owner: AdminUserRow | null;
  ownerMayUse: boolean;
  canEdit: boolean;
  busy: boolean;
  onOpenThread: (environmentId: EnvironmentId) => void;
  onDestroy: () => void;
}) {
  const { row, owner } = props;
  const [snapshots, setSnapshots] = useState<AdminEnvironmentSnapshots | null>(null);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const url = previewUrl(row);

  const loadSnapshots = async () => {
    if (loadingSnapshots) return;
    if (snapshots !== null) {
      setSnapshots(null);
      return;
    }
    setLoadingSnapshots(true);
    setSnapshotError(null);
    try {
      setSnapshots(await EnvironmentsClient.snapshots(row.id));
    } catch (err) {
      setSnapshotError(err instanceof Error ? err.message : "No se pudieron listar las copias");
    } finally {
      setLoadingSnapshots(false);
    }
  };

  return (
    <>
      <tr className="border-b border-border/30 align-top last:border-b-0">
        <td className="px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <UserAvatar
              userId={(row.userId ?? "") as string}
              displayName={owner?.name ?? "Usuario desconocido"}
              avatarFileName={owner?.avatarFileName ?? null}
              size="sm"
              className="shrink-0"
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{owner?.name ?? "Usuario desconocido"}</span>
              <span className="truncate text-xs text-muted-foreground">{owner?.email ?? ""}</span>
            </div>
          </div>
        </td>
        <td className={`px-4 py-3 text-xs ${STATUS_CLASS[row.status]}`}>
          {environmentStatusLabel(row.status)}
          {row.failureReason !== null ? (
            <div className="mt-1 text-[11px] text-muted-foreground">{row.failureReason}</div>
          ) : null}
          {row.status === "ready" && !props.ownerMayUse ? (
            <div className="mt-1 text-[11px] text-amber-400">
              Sin usar: falta darle <code>p.env.use</code> en este proyecto, en Usuarios.
            </div>
          ) : null}
        </td>
        <td className="px-4 py-3 text-xs">
          <div className="font-mono">{row.branch}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">desde {row.baseRef}</div>
        </td>
        <td className="px-4 py-3 text-xs">
          {url === null ? (
            <span className="text-muted-foreground">Sin arrancar</span>
          ) : (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline underline-offset-2"
            >
              {row.devHostname}
            </a>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-right">
          <Button variant="outline" size="sm" onClick={() => void loadSnapshots()}>
            {snapshots === null ? "Copias" : "Ocultar"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="ml-2"
            disabled={row.status !== "ready"}
            onClick={() => props.onOpenThread(row.id)}
          >
            Abrir hilo aquí
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="ml-2 text-red-500 hover:text-red-400"
            disabled={!props.canEdit || props.busy}
            onClick={props.onDestroy}
          >
            {props.busy ? "Destruyendo…" : "Destruir"}
          </Button>
        </td>
      </tr>
      {loadingSnapshots || snapshots !== null || snapshotError !== null ? (
        <tr className="border-b border-border/30 last:border-b-0">
          <td colSpan={5} className="bg-muted/10 px-4 py-3">
            {loadingSnapshots ? (
              <p className="text-xs text-muted-foreground">Leyendo copias…</p>
            ) : snapshotError !== null ? (
              <p className="text-xs text-red-300">{snapshotError}</p>
            ) : snapshots === null ? null : (
              <SnapshotList snapshots={snapshots} />
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ProvisionDialog(props: {
  projectId: ProjectId;
  projectName: string;
  candidates: ReadonlyArray<{ userId: string; name: string; email: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [userId, setUserId] = useState<string>(props.candidates[0]?.userId ?? "");
  const [baseBranch, setBaseBranch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    if (userId === "") {
      setError("Elige a quién es el entorno.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await EnvironmentsClient.provision({
        projectId: props.projectId,
        userId: userId as UserId,
        baseBranch: baseBranch.trim(),
      });
      props.onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo crear el entorno");
    } finally {
      setSubmitting(false);
    }
  };

  const selected = props.candidates.find((candidate) => candidate.userId === userId) ?? null;

  return (
    <ModalShell title={`Nuevo entorno en ${props.projectName}`} onClose={props.onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field label="Dueño">
          {props.candidates.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Todo el mundo tiene ya un entorno en este proyecto. Destruye uno para volver a
              crearlo.
            </p>
          ) : (
            <Select value={userId} onValueChange={(value) => value && setUserId(value)}>
              <SelectTrigger className="w-full" aria-label="Dueño del entorno">
                <SelectValue>
                  {selected === null ? "Elige a alguien" : `${selected.name} — ${selected.email}`}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="start" alignItemWithTrigger={false}>
                {props.candidates.map((candidate) => (
                  <SelectItem hideIndicator key={candidate.userId} value={candidate.userId}>
                    {candidate.name} — {candidate.email}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          )}
        </Field>

        <Field label="Rama base (opcional)">
          <Input
            value={baseBranch}
            onChange={(e) => setBaseBranch(e.target.value)}
            placeholder="main"
          />
        </Field>

        <p className="text-[11px] text-muted-foreground">
          Crear el entorno levanta su worktree y ejecuta el hook de preparación del proyecto
          (instalar, migrar, sembrar), que puede tardar minutos. La ventana responde en cuanto el
          worktree existe; el estado pasa a «Listo» cuando el hook termina.
        </p>

        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={props.onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={submitting || props.candidates.length === 0}>
            {submitting ? "Creando…" : "Crear entorno"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
